<template>
  <div
    class="h-100 d-flex flex-column"
    :class="{ 'navbar--compact': effectiveCompactNavbar }"
  >
    <!-- logo -->
    <app-logo :dark="darkMode" :compact="effectiveCompactNavbar" />

    <v-chip class="mx-1 justify-center" color="primary" pill x-small>
      {{ userBadgeValue() }}
    </v-chip>

    <!-- top menu -->
    <v-list :dark="darkMode" flat>
      <v-list-item
        v-if="wsOpenStatus !== wsStatus && !is3rdPartyUrl"
        class="mb-2 connection-status-item"
      >
        <v-tooltip right>
          <template v-slot:activator="{ on, attrs }">
            <div
              class="connection-indicator mx-auto"
              v-bind="attrs"
              v-on="on"
              @click="$root.$emit('show-connection-dialog')"
              style="cursor: pointer"
            >
              <span class="status-dot" />
              <v-icon small :color="darkMode ? 'grey lighten-1' : 'grey darken-1'">
                mdi-wifi-off
              </v-icon>
            </div>
          </template>
          <span>Server disconnected{{ isAuth ? "" : " — log in to connect" }}</span>
        </v-tooltip>
      </v-list-item>

      <!-- imports -->
      <div class="my-4">
        <!-- TODO close-on-click not working -->
        <!-- eager to automatically open import modal in free mode -->
        <v-menu
          content-class="menu-content"
          eager
          offset-x
          v-model="importExamsMenuOpen"
        >
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <v-list-item
                v-if="$route.name !== 'share'"
                id="IM"
                :disabled="$isDemoUser || !hasEnoughSpace()"
              >
                <navigation-item
                  :item="{ icon: 'mdi-plus', name: 'import exams' }"
                />
                <!-- :disabled="isDemoOrFree() || !hasEnoughSpace()" -->
              </v-list-item>
            </div> </template
          ><!-- v-if="!isDemoOrFree() && hasEnoughSpace()" -->
          <div class="menu-content white-aura-of-coke">
            <v-list
              :dark="darkMode"
              dense
              :color="darkMode ? '#2c2c2c' : '#FFFFFF'"
              elevation="24"
            >
              <import-exams
                :dark="darkMode"
                @close-import-exams-menu="importExamsMenuOpen = false"
              />
              <import-pacs :dark="darkMode" :disabled="!isPro()" />
            </v-list>
          </div>
          <!-- <div v-else>
            <subscription-alert />
          </div> -->
        </v-menu>
      </div>
      <div v-if="$route.name !== 'share'">
        <template v-for="route in topRoutes">
          <v-menu
            content-class="menu-content"
            v-if="isDisabledRoute(route)"
            :key="route.name"
            offset-x
            tile
          >
            <template v-slot:activator="{ on, attrs }">
              <slot v-bind="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="menu-content">
                  <v-list-item
                    elevation="24"
                    class="px-1"
                    disabled
                    :to="{ name: route.name }"
                    two-line
                    :id="`NB-${route.name}`"
                  >
                    <component
                      v-if="route.mainMenuLinkComponent"
                      :is="route.mainMenuLinkComponent"
                      disabled
                      :item="route"
                    />
                    <navigation-item
                      :dark="darkMode"
                      v-else
                      disabled
                      :item="route"
                    />
                  </v-list-item>
                </div>
              </slot>
            </template>

            <subscription-alert />
          </v-menu>

          <v-list-item
            v-else
            :key="`else-${route.name}`"
            class="px-1"
            :to="{ name: route.name }"
            two-line
            :id="`NB-${route.name}`"
            :dark="darkMode"
            :disabled="isItemDisabled(route.name)"
          >
            <template v-slot="{ active }">
              <component
                v-if="route.mainMenuLinkComponent"
                :is="route.mainMenuLinkComponent"
                :active="active"
                :item="route"
              />
              <navigation-item :active="active" :item="route" />
            </template>
          </v-list-item>
        </template>
      </div>
      <div v-else>
        <v-list-item class="px-1" two-line :id="`NB-share`">
          <template v-slot="{ active }">
            <navigation-item :active="active" :item="shareMenuItem" />
          </template>
        </v-list-item>
      </div>
    </v-list>

    <!-- bottom menu -->
    <v-list class="mt-auto pb-0" :dark="darkMode" dense flat>
<v-list-item
        v-if="!is3rdPartyUrl"
        class="px-1 mb-2"
        :disabled="$route.name !== 'share' && isDisabledRoute(reportBugRoute)"
        :to="{ name: reportBugRoute.name, params: { buildId: buildId } }"
        two-line
        :id="`NB-${reportBugRoute.name}`"
      >
        <template v-slot="{ active }">
          <navigation-item :active="active" :item="reportBugRoute" />
        </template>
      </v-list-item>
      <v-list-item
        v-if="!is3rdPartyUrl"
        class="px-1 mb-2"
        :disabled="$route.name !== 'share' && isDisabledRoute(helpRoute)"
        :to="{ name: helpRoute.name }"
        two-line
        :id="`NB-${helpRoute.name}`"
      >
        <template v-slot="{ active }">
          <navigation-item :active="active" :item="helpRoute" />
        </template>
      </v-list-item>

      <v-list-item class="px-1">
        <template v-slot="{ active }">
          <build-info :active="active" :item="buildInfo" />
        </template>
      </v-list-item>

      <!-- personal account panel -->
      <!-- TODO: resore this to activate settings page :settings-route-name="settingsRoute.name"
        :to="{ name: homeRoute.name }" -->
      <v-list-item
        v-if="$route.name === 'share'"
        class="px-1"
        target="_blank"
        two-line
        :id="`NB-${homeRoute.name}`"
        @click="shareLoginButtonClicked"
      >
        <template v-slot="{ active }">
          <navigation-item :active="active" :item="homeRoute" />
        </template>
      </v-list-item>
      <auth-account-panel
        :dark="darkMode"
        v-else-if="!is3rdPartyUrl"
        direction="right"
      >
        <template v-slot="{ on, attrs }">
          <v-list-item
            class="account-list-item px-1"
            two-line
            v-bind="attrs"
            v-on="on"
            :id="`NB-${accountRoute.name}`"
          >
            <navigation-item :item="accountRoute" />
          </v-list-item>
        </template>
      </auth-account-panel>
      <!-- customer logo -->
      <customer-logo />
    </v-list>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { statusSource as websocketStatusSource } from "@/js/download/seriesDownload";

import AppLogo from "@/components/AppLogo";
import CustomerLogo from "@/components/CustomerLogo";
import ImportExams from "@/components/ImportExams";
import ImportPacs from "@/components/ImportPacs.vue";
import NavigationItem from "@/components/NavigationItem";
import SubscriptionAlert from "@/components/SubscriptionAlert";
import buildData from "@/../BUILD_ID.json";
import BuildInfo from "@/components/Info.vue";

const FORCED_COMPACT_MAX_WIDTH = 960;

export default {
  name: "NavigationBar",
  components: {
    AppLogo,
    CustomerLogo,
    ImportExams,
    ImportPacs,
    NavigationItem,
    SubscriptionAlert,
    BuildInfo
  },
  data() {
    return {
      settingsRoute: {
        name: "settings"
      },
      reportBugRoute: {
        name: "report bug",
        icon: "mdi-bug"
      },
      helpRoute: {
        name: "help",
        icon: "mdi-help-circle"
      },
      homeRoute: {
        name: "login",
        icon: "mdi-home-account"
      },
      shareMenuItem: {
        name: "share link",
        icon: "mdi-image-filter-black-white"
      },
      buildInfo: {
        name: "info",
        icon: "mdi-information-outline"
      },
      topRoutes: this.$router.options.routes.filter(r => r.mainMenuLink),
      wsOpenStatus: WebSocket.OPEN,
      wsStatus: null,
      wsSubscription: null,
      is3rdPartyUrl: process.env.APP_CUSTOMER.getExternal3rdPartyURL,
      importExamsMenuOpen: false,
      buildId: buildData ? buildData.build_id : null
    };
  },
  beforeMount() {
    if (this.is3rdPartyUrl) {
      this.topRoutes = this.topRoutes.filter(route => route.name == "viewer");
    }
  },
  mounted() {
    // listen to ws connection status changes
    this.wsSubscription = websocketStatusSource.subscribe({
      next: value => (this.wsStatus = value)
    });
  },
  beforeDestroy() {
    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
    }
  },
  computed: {
    ...mapGetters("auth", ["isAuth"]),
    ...mapGetters(["darkMode", "compactNavbar"]),
    ...mapGetters("dashboard", ["numberOfSelectedSeries"]),
    ...mapGetters("viewer", { viewerNumberOfSeries: "numberOfSeries" }),
    isForcedCompactNavbar() {
      const { width, height } = this.$vuetify.breakpoint;
      return width <= FORCED_COMPACT_MAX_WIDTH && height > width;
    },
    effectiveCompactNavbar() {
      return this.compactNavbar || this.isForcedCompactNavbar;
    },
    accountRoute() {
      return {
        name: "account",
        icon: this.isAuth ? "mdi-account" : "mdi-account-lock"
      };
    },
  },
  methods: {
isDisabledRoute(route) {
      const user = this.$store.state.auth.user;

      // enable the "viewer" route for free-tier users
      if (route.name === "viewer" && user?.subscription_tier === "free") {
        return false;
      }
      // Navbar item disable rule
      if (route.disabled) {
        return true;
      }
      if (this.$route.name === "share") {
        return true;
      }
      // Auth route
      const r = this.$router.options.routes.find(
        ({ name }) => name == route.name
      );

      if (!r) {
        return true;
      }

      const authRequired = r.meta ? r.meta.auth : false;
      return authRequired ? !this.isAuth : false;
    },
    isPro() {
      return this.$store.state.auth.user?.subscription_tier === "pro";
    },
    showDemoUserBadge() {
      return this.$isDemoUser && this.$route.name !== "share";
    },
    userBadgeValue() {
      const user = this.$store.state.auth.user;

      if (!user) return "FREE"; // Handle the case where user is undefined/null

      if (user.is_admin) return "ADMIN";

      const validTiers = ["pro", "free", "base"];
      if (user.subscription_tier === "free") return "FREE";
      if (validTiers.includes(user.subscription_tier))
        return user.subscription_tier.toUpperCase();

      if (this.$route.name === "share") return "SHARED";

      return this.isAuth ? "FREE" : "GUEST"; // Default fallback
    },
    showUserBadge() {
      return true;
    },
    shareLoginButtonClicked() {
      this.$store.dispatch("auth/logout");
      this.$router.push({ name: this.homeRoute.name });
    },
    isItemDisabled(routeName) {
      const user = this.$store.state.auth.user;

      if (routeName === "viewer") {
        // Always disable for free-tier users
        if (user?.subscription_tier === "free") {
          return true;
        }
        // Disable if no series is selected or loaded in viewer (but only for non-free users)
        if (!this.numberOfSelectedSeries && !this.viewerNumberOfSeries) {
          return true;
        }
      }

      return false;
    },
    hasEnoughSpace() {
      const user = this.$store.state.auth.user;

      if (!user) return false; // Prevents errors if user data is missing

      const tierFallbackMemory = {
        free: 1 * 10 ** 9,
        base: 5 * 10 ** 9,
        pro: 100 * 10 ** 9
      };

      const includedMemory =
        (user.memory_included || 0) ||
        tierFallbackMemory[(user.subscription_tier || "").toLowerCase()] ||
        0;
      const totalMemory = includedMemory + (user.memory_bonus || 0);
      const usedMemory = user.memory_used || 0;

      return totalMemory > usedMemory;
    }
  }
};
</script>

<style lang="scss" scoped>
.v-navigation-drawer {
  font-family: $highlights-font-family;
}

.v-list-item:not(.v-list-item--active):hover {
  opacity: 0.7;
}

.v-list-item--active {
  background: white;
}

.account-list-item[aria-expanded="true"] {
  opacity: 0.7;
}

.navbar--compact ::v-deep .nav-item-label {
  display: none;
}

.navbar--compact ::v-deep .v-chip {
  font-size: 0.5rem;
  height: 14px;
  padding: 0 4px;
}

.connection-status-item {
  min-height: unset;
}

.connection-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 50%;

  .status-dot {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #f2911b;
    animation: pulse 2s ease-in-out infinite;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}
</style>

<style lang="scss">
.header-group {
  height: 100%;
  display: flex;
  flex-direction: column;

  .header-title {
    height: 1rem;
    overflow: hidden;
    text-align: center;
    text-transform: uppercase;

    // text-caption
    font-size: xx-small;
    font-weight: 100;
  }

  .header-content {
    display: flex;
    padding: 0 0.5em;
    margin: auto;
  }
}
.switch {
  margin-top: 2px;
  margin-bottom: 2px;
}
.menu-content {
  background-color: #1e1e1e;
}
.mobile-menu-content {
  max-height: calc(100% - 80px);
  overflow: auto;
}
</style>
